import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { BarChart, Delete, Link as LinkIcon } from '@mui/icons-material'
import { Alert, Autocomplete, Box, Divider, ListSubheader, TextField, Typography } from '@mui/material'
import { GridActionsCellItem } from '@mui/x-data-grid-pro'

import { AIObjectPropertyType } from '@tabeeb/contracts/microservices/swagger-ai-service'
import { debounce, uniqueId } from 'lodash'

import { ConfirmationDialog, ResizableDialog, TextWithLinks } from '@collaborate-health/uikit'
import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { getCurrentUserId } from '@tabeeb/modules/account/selectors'
import ReportTemplateEditor from '@tabeeb/modules/reportTemplateEditor'
import CircularProgressBar from '@tabeeb/modules/shared/tabeebCircularProgressBar'
import { useDialogState } from '@tabeeb/modules/shared/utils/hooks'

import { deleteUniqueAiObject } from '@tabeeb/modules/artificialIntelligence/actions'

import {
  getAIReport,
  getAIReportAiObjects,
  getAreAIObjectsLoading,
  getIsAIReportDialogOpen,
  getIsAnalysisLoading,
  getIsRefreshNeeded,
  getSelectedAiObjectId,
  getSelectedTemplate,
  getTemplates,
} from '../../selectors'
import {
  closeAiReportDialog,
  createPublishTemplate,
  deletePublishTemplate,
  getAiObjectAnalysis,
  getAiObjectsByContent,
  goToAnnotation,
  selectTemplate,
  updatePublishTemplate,
  updateUniqueAiObjectFromJson,
} from '../../actions'
import AIReportDialogHeader from '../AIReportDialogHeader'
import Tabs from '../Tabs'
import PublishSettingsDialog from '../PublishSettingsDialog'
import { DialogContentContainer, LinksMenu, LinksMenuItem } from './styles'

const AIReportDialog = () => {
  const dispatch = useDispatch()

  const [anchorPosition, setAnchorPosition] = useState({ left: 0, top: 0 })
  const [selectedUniqueAiObject, setSelectedUniqueAiObject] = useState(null)
  const [isInTemplateEditMode, setIsInEditMode] = useState(false)
  const [rowSelectionModel, setRowSelectionModel] = useState([])

  const isOpen = useSelector(getIsAIReportDialogOpen)
  const contentId = useSelector(getContentId)
  const userId = useSelector(getCurrentUserId)
  const aiObjects = useSelector(getAIReportAiObjects)
  const analysis = useSelector(getAIReport)
  const selectedAiObjectId = useSelector(getSelectedAiObjectId)
  const areAIObjectsLoading = useSelector(getAreAIObjectsLoading)
  const isAnalysisLoading = useSelector(getIsAnalysisLoading)
  const templates = useSelector(getTemplates)

  const selectedTemplate = useSelector(getSelectedTemplate)
  const isRefreshNeeded = useSelector(getIsRefreshNeeded)
  const hasGroupedColumns = selectedTemplate.columns?.some((c) => c.grouping > 0) || false
  const areHyperLinksHidden = isInTemplateEditMode || hasGroupedColumns
  const isDataEditingEnabled = !isInTemplateEditMode && !hasGroupedColumns && !isRefreshNeeded
  const showAlert = isRefreshNeeded

  const [deleteAnnotationsDialogOpen, openDeleteAnnotationsDialog, onDeleteAnnotationsDialogClose] = useDialogState()
  const handleLinkMenuClick = useCallback((event, links) => {
    setAnchorPosition({ top: event.pageY, left: event.pageX })
    setSelectedUniqueAiObject(links)
  }, [])

  const handleDeleteAnnotationsClick = useCallback(
    (event, row) => {
      setSelectedUniqueAiObject(row)
      openDeleteAnnotationsDialog()
    },
    [openDeleteAnnotationsDialog]
  )

  const dataGridColumns = useMemo(() => {
    const actionColumn = {
      field: '__actions__',
      type: 'actions',
      headerName: '',
      width: 80,
      getActions: (params) => {
        const actions = []

        if (rowSelectionModel.includes(params.id)) {
          actions.push(
            <GridActionsCellItem
              icon={<Delete color='primary' />}
              onClick={(e) => handleDeleteAnnotationsClick(e, params.row)}
              label='Delete'
            />
          )
        }

        if (!areHyperLinksHidden) {
          actions.push(
            <GridActionsCellItem
              icon={<LinkIcon color='primary' />}
              onClick={(e) => {
                e.stopPropagation()
                handleLinkMenuClick(e, params.row)
              }}
              label='Link'
            />
          )
        }

        return actions
      },
    }

    const otherColumns =
      analysis?.columns?.map((c) => {
        const baseColumnDef = {
          field: c.Name,
          editable: c.Editable,
          width: 200,
          filterable: false,
          sortable: false,
          renderHeader: (params) => <strong>{params.colDef.headerName || params.field}</strong>,
          valueGetter: (params) => params.row.UniqueAIObject[params.field],
        }

        if (c.Name === 'Name') {
          return {
            ...baseColumnDef,
            renderCell: (cell) => <span>{cell.row.UniqueAIObject[cell.field]}</span>,
          }
        }

        if (c.Type === AIObjectPropertyType.Dropdown) {
          return {
            ...baseColumnDef,
            renderCell: (cell) => {
              let cellValue = ''
              const rawValue = cell.row.UniqueAIObject[cell.field]
              if (hasGroupedColumns) {
                cellValue = rawValue
              } else if (rawValue) {
                try {
                  const parsed = JSON.parse(rawValue)
                  cellValue = parsed[0]?.Value ?? ''
                } catch {
                  cellValue = ''
                }
              }
              return <span>{cellValue}</span>
            },
            renderEditCell: (params) => (
              <Autocomplete
                sx={{ width: '100%' }}
                options={c.SelectionList.map((option) => JSON.stringify([option]))}
                getOptionLabel={(option) => JSON.parse(option)[0].Value}
                renderInput={(p) => <TextField {...p} label={c.Name} variant='outlined' />}
                value={params.value}
                onChange={(event, newValue) => {
                  params.api.setEditCellValue({
                    id: params.id,
                    field: params.field,
                    value: newValue,
                  })
                }}
              />
            ),
          }
        }

        return {
          ...baseColumnDef,
          renderCell: (cell) => (
            <TextWithLinks>
              <span>{cell.row.UniqueAIObject[cell.field]}</span>
            </TextWithLinks>
          ),
        }
      }) || []

    return [actionColumn, ...otherColumns]
  }, [
    analysis?.columns,
    areHyperLinksHidden,
    handleDeleteAnnotationsClick,
    handleLinkMenuClick,
    hasGroupedColumns,
    rowSelectionModel,
  ])

  const availableColumns = useMemo(
    () =>
      analysis?.availableColumns?.map((c) => ({
        field: c.Name,
      })) || [],
    [analysis?.availableColumns]
  )

  const onTemplateSet = (template) => {
    dispatch(selectTemplate(template))
  }

  const onTemplateAdd = (template) => {
    const templateModel = {
      AIObjectId: selectedAiObjectId,
      name: template.name,
      columns: template.columns.map((c) => ({
        property: c.field,
        sorting: c.sorting,
        filter: c.filter,
        grouping: c.grouping,
      })),
    }
    dispatch(createPublishTemplate.request({ template: templateModel }))
  }

  const onTemplateSave = (template) => {
    const templateModel = {
      name: template.name,
      id: template.id,
      columns: template.columns.map((c) => ({
        sorting: c.sorting,
        filter: c.filter,
        grouping: c.grouping,
        property: c.field,
        id: c.id,
      })),
    }
    onTemplateSet(template)
    dispatch(updatePublishTemplate.request({ template: templateModel }))
  }

  const handleLinkMenuClose = () => {
    setAnchorPosition({ top: 0, left: 0 })
  }

  const handleClose = (_, reason) => {
    if (reason && reason === 'backdropClick') {
      return
    }
    dispatch(closeAiReportDialog())
  }

  const handleProcessRowUpdate = useCallback(
    (newRow, row) => {
      const editableColumns = dataGridColumns.filter((c) => c.editable).map((c) => c.field)

      const properties = {}

      editableColumns.forEach((c) => {
        properties[c] = newRow[c]
      })

      const model = {
        ContentId: contentId,
        AIObjectId: selectedAiObjectId,
        Id: newRow.UniqueAIObjectId,
        Name: newRow.UniqueAIObject.Name,
        UserId: userId,
        Properties: JSON.stringify(properties),
        Source: AIReportDialog.name,
      }

      dispatch(updateUniqueAiObjectFromJson.request(model))

      return { ...row, UniqueAIObject: { ...row.UniqueAIObject, ...properties } }
    },
    [dataGridColumns, contentId, dispatch, selectedAiObjectId, userId]
  )

  const handleSelectAnnotation = (link) => {
    dispatch(goToAnnotation(link))
    handleLinkMenuClose()
  }

  const rows = useMemo(() => analysis?.data.map((r) => ({ ...r, mui_uid: uniqueId() })) || [], [analysis?.data])
  const isAnalysisEmpty = !rows.length && !analysis?.columns

  const previewReadProps = useMemo(
    () => ({
      editMode: 'row',
      processRowUpdate: handleProcessRowUpdate,
      onProcessRowUpdateError: (error) => console.error(error),
      experimentalFeatures: { newEditingApi: true },
      hideFooterPagination: true,
      hideFooterSelectedRowCount: true,
      isCellEditable: () => isDataEditingEnabled,
      onRowSelectionModelChange: (newRowSelectionModel) => {
        setRowSelectionModel(newRowSelectionModel)
      },
      rowSelectionModel,
    }),
    [handleProcessRowUpdate, isDataEditingEnabled, rowSelectionModel]
  )

  const debouncedAiObjectAnalysisRequest = useRef(
    debounce((query) => {
      dispatch(getAiObjectAnalysis.cancel())
      dispatch(getAiObjectAnalysis.request(query))
    }, 300)
  ).current

  const getAiObjectAnalysisDebounced = useCallback(
    (data) => {
      if (selectedAiObjectId) {
        debouncedAiObjectAnalysisRequest({
          contentId,
          aiObjectId: selectedAiObjectId,
          columns: selectedTemplate.columns?.map((c) => ({ ...c, property: c.field })),
          ...data,
        })
      }
    },
    [contentId, debouncedAiObjectAnalysisRequest, selectedAiObjectId, selectedTemplate.columns]
  )

  useEffect(() => {
    if (isOpen) {
      dispatch(getAiObjectsByContent.request({ contentId }))
    }
  }, [isOpen, dispatch, contentId])

  return (
    <>
      <ResizableDialog
        allowBackgroundInteraction
        open={isOpen}
        onClose={handleClose}
        header={
          !isAnalysisEmpty && !areAIObjectsLoading ? (
            <AIReportDialogHeader
              isInEditMode={isInTemplateEditMode}
              getAiObjectAnalysisDebounced={getAiObjectAnalysisDebounced}
            />
          ) : (
            <div />
          )
        }
        content={
          <>
            {(areAIObjectsLoading || (isAnalysisEmpty && isAnalysisLoading)) && <CircularProgressBar />}
            {isAnalysisEmpty && !areAIObjectsLoading && !isAnalysisLoading && (
              <Box m='auto' textAlign='center'>
                <BarChart sx={{ typography: 'h3' }} />
                <Typography>No data has been found...</Typography>
              </Box>
            )}
            {!isAnalysisEmpty && !areAIObjectsLoading && (
              <DialogContentContainer>
                {showAlert && !isAnalysisLoading ? (
                  <Alert severity='warning'>
                    The data has been changed. To see actual data and proceed with further editing, kindly refresh the
                    report.
                  </Alert>
                ) : null}
                <Box overflow='auto' flex={1}>
                  <ReportTemplateEditor
                    rows={rows}
                    columns={dataGridColumns}
                    availableColumns={availableColumns}
                    previewReadProps={previewReadProps}
                    isInEditMode={isInTemplateEditMode}
                    setIsInEditMode={setIsInEditMode}
                    isLoading={isAnalysisLoading}
                    template={selectedTemplate}
                    templates={templates}
                    onTemplateSet={onTemplateSet}
                    onTemplateAdd={onTemplateAdd}
                    onTemplateSave={onTemplateSave}
                    onTemplateDelete={(template) => {
                      onTemplateSet({ id: 0, columns: null })
                      dispatch(deletePublishTemplate.request({ id: template.id }))
                    }}
                  />
                </Box>
                {!isInTemplateEditMode && aiObjects.length > 0 && selectedAiObjectId && (
                  <Tabs tabs={aiObjects} isLoading={false} />
                )}
                <LinksMenu
                  anchorPosition={anchorPosition}
                  open={Boolean(anchorPosition.left)}
                  onClose={handleLinkMenuClose}
                  anchorReference='anchorPosition'
                >
                  <ListSubheader>
                    <Typography>Associated pages</Typography>
                    <Divider />
                  </ListSubheader>
                  {selectedUniqueAiObject?.Hyperlinks?.map((h) => (
                    <LinksMenuItem title={h.PageName} key={h.AnnotationId} onClick={() => handleSelectAnnotation(h)}>
                      <Typography noWrap>{h.PageName}</Typography>
                    </LinksMenuItem>
                  ))}
                </LinksMenu>
                <PublishSettingsDialog />
              </DialogContentContainer>
            )}
          </>
        }
      />

      <ConfirmationDialog
        title={`Deleting ${selectedUniqueAiObject?.UniqueAIObject?.Name} will permanently remove all annotations associated with this AI object from the session.`}
        submitButtonText='Delete'
        isOpen={deleteAnnotationsDialogOpen}
        onCloseDialog={onDeleteAnnotationsDialogClose}
        onSubmit={() => {
          dispatch(
            deleteUniqueAiObject.request({
              contentId,
              uniqueAIObjectId: selectedUniqueAiObject.UniqueAIObjectId,
            })
          )
          onDeleteAnnotationsDialogClose()
        }}
      />
    </>
  )
}

export default AIReportDialog
